import { useAtom } from "jotai";
import { atomWithHash } from "jotai/utils";
import React, { useState } from "react";
import LiveFilters from "./LiveFilters";
import { Count } from "./Count";
import { Map } from "./Map";
import { Pagination } from "./Pagination";
import { Results } from "./Results";
import { ISearchContext, SearchContextProvider } from "./SearchContext";
import SortContainer from "./SortContainer";

export interface ISearchCompactProps {
    /**
     * An array of arbitrary items in the search index.
     */
    index: any[];
    /**
     * Classes to add to the search container div.
     */
    className?: string;
    /**
     * Function to render items in search results.
     */
    renderItem: (arg0: any, index: number) => React.ReactNode;

    /**
     * Results per page, or 0 for All
     */
    pageSize: number;

    /**
     * Include a Google Map results?
     */
    useMap?: boolean;

    /**
     * Google Map mapId parameter
     */
    mapId?: string;

    /**
     * Include a Count component to show current number of results and page visible?
     */
    showCount?: boolean;

    /**
     * Filter nodes to display in the standard filters section of the search.
     */
    primaryFilters?: (context: ISearchContext) => React.ReactNode;

    /**
     * Class name(s) to apply to the filter wrapper
     */
    filterClassName?: string;

    /**
     * Class name(s) to apply to the filter wrapper
     */
    formClassName?: string;

    /**
     * Class name(s) to apply to the pagination control wrapper
     */
    pagerClassName?: string;
    /**
     * Class name(s) to apply to the map control wrapper
     */
    mapClassName?: string;
    /**
     * Class name(s) to apply to the results wrapper
     */
    resultsClassName?: string;
    /**
     * Text for the link to show advanced filters.
     */
    showAdvancedText?: string;

    /**
     * Text for the link to hide advanced filters.
     */
    hideAdvancedText?: string;

    /**
     * Hide alternate filters ("collapsible" advanced search).
     */
    hideAlternateFiters?: boolean;

    /**
     * Text for the link to show the map.
     */
    showMapText?: string;

    /**
     * Click handler to fire when a map marker for an item in the index
     * is clicked.
     */
    mapClick?: (arg: any) => void;

    /**
     * Text for the link to hide the map.
     */
    hideMapText?: string;

    /**
     * Configured Sort component for exposed sort options in the search.
     */
    sort?: (context: ISearchContext) => React.ReactNode;
    /**
     * Initial Filter if provided
     */
    initialFilter?: string
    /**
     * Default sort if provided
     */
    defaultSort?: string;
    /**
     * Search form background image
     */
    formBackgroundImage?: string;
    /**
     * Search form title
     */
    title?: string;
    /**
     * Search layout
     */
    forceMapShow?: boolean;
    preRenderItem?: (arg0: any, index: number) => void;
    renderFunction?: (arg0: any, searchContext?: ISearchContext) => void;
}

export const SearchCompact: React.FC<ISearchCompactProps> = ({
    index,
    renderItem,
    useMap = false,
    mapClick,
    pageSize,
    showCount = false,
    primaryFilters,
    showMapText = "Show Map",
    hideMapText = "Hide Map",
    sort,
    mapId,
    className,
    filterClassName,
    formClassName,
    pagerClassName,
    resultsClassName,
    mapClassName,
    initialFilter,
    defaultSort,
    title,
    forceMapShow = false,
    preRenderItem,
    renderFunction,
}) => {
    const [showMapAtom, setShowMapAtom] = useState(() =>
        atomWithHash("showMap", forceMapShow, { replaceState: true })
    );
    const [showMap, setShowMap] = useAtom(showMapAtom);
    /*Page size type check*/
    pageSize = typeof pageSize === "string" ? parseInt(pageSize) : pageSize;
    return (
        <SearchContextProvider index={index} pageSize={pageSize} initialFilter={initialFilter} initialSort={defaultSort}>
            <div className={`search ${className}`}>
                <LiveFilters
                    className={filterClassName}
                    formClassName={formClassName}
                    primaryFilters={primaryFilters}
                />
                <div className={`${showMap ? "lg:h-[90vh] xl:justify-end" : "lg:h-auto xl:justify-center max-w-screen-2xl"} h-screen relative w-full flex flex-col lg:flex-row ${resultsClassName}`}>
                    <div className={`${showMap ? "lg:w-1/2 max-w-screen-md lg:px-4 lg:overflow-y-scroll" : "w-full max-w-screen-2xl xl:self-center lg:pl-4 lg:pr-14 overflow-y-scroll lg:overflow-y-visible"} px-2 py-3.5 flex flex-col`}>
                        <h4 className="text-2xl font-bold">{title}</h4>
                        <div className="w-full flex flex-col items-start md:flex-row md:justify-between py-2">
                            {showCount && <Count className="text-left font-bold py-1" />}
                            <SortContainer
                                sort={sort}
                            />
                        </div>
                        <Results
                            className={`${showMap ? "hidden lg:grid xl:grid-cols-2" : "grid md:grid-cols-2 xl:grid-cols-4"} gap-4 items-start ${resultsClassName} search-results`}
                            renderItem={renderItem}
                            preRenderItem={preRenderItem}
                            renderFunction={renderFunction}
                        />
                        <div className={`${showMap ? "hidden lg:block" : "mb-11 lg:mb-0"} w-full`}>
                            <Pagination
                                className={`my-6 ${pagerClassName} pagination pagination-bottom`}
                            />
                        </div>
                    </div>
                    <div className={`${showMap ? "block" : "hidden"} relative h-full w-full lg:w-1/2`}>
                        {useMap && (
                            <Map
                                mapId={mapId}
                                className={`${mapClassName} h-full search-map`}
                                mapClick={mapClick}
                                mapProps={{
                                    mapContainerStyle: {
                                        height: "100%",
                                        width: "100%",
                                    }
                                }}
                            />
                        )}
                    </div>
                    <button onClick={(e) => { setShowMap(!showMap) }} type="button" className="self-center absolute bottom-9 lg:bottom-[unset] lg:-right-10 lg:top-36 bg-button-bg text-button-text px-5 py-2.5 lg:-rotate-90 shadow-md">
                        {showMap ? hideMapText : showMapText}
                    </button>
                </div>
            </div>
        </SearchContextProvider>
    );
};

export default SearchCompact;
